/**
 * The diner's Menu, Item, Cart and Checkout (ORDR-165, ORDR-166).
 *
 * The app draws every one of them, composing framework primitives (why:
 * `MenuScreen.tsx`, `../checkout/CheckoutScreen.tsx`). ORDR-165's interim seam,
 * which handed the lines to the framework's `CustomerOrdering` through
 * `handoff`, is gone: that path could not take pay in store or the kitchen's
 * stages, and dropped the diner's answers on the way to commerce.
 *
 * ONE TREE FROM LOADING TO THE MENU. Until the services exist (`null`), only
 * the header is drawn, in the same place it holds above the menu, so a notice
 * the diner is reading is not torn down and redrawn when the menu arrives.
 *
 * THE LINES LIVE HERE, and the services keep the server cart level with them:
 * a line sent once is not sent again, a changed quantity replaces it, and a
 * removed line is removed. Every total shown is the server's quote.
 *
 * ONE QUOTE PER CHANGE (ORDR-228). The Menu quotes the cart when its lines
 * change; opening the Cart shows that quote rather than asking again. It used
 * to ask again on every view change, and a diner who tapped View cart and then
 * Checkout at once had that second quote still in flight when Checkout asked
 * for its own: two quote GETs on entering Checkout, measured in production.
 * Checkout quotes once on entry, opens on the last known total marked as
 * updating, and hands each fresh figure back here, so the Menu shows what the
 * diner last saw. A mode changed at Checkout drops the figure here until the
 * new mode is priced, so the Menu never shows the other mode's total as
 * settled.
 */

import { Suspense, lazy, useCallback, useEffect, useRef, useState, type ReactElement, type ReactNode } from "react";
import type { CardPayment, PlacedOrder } from "../checkout/CheckoutScreen.js";
import { cartSignature } from "../customer/cartSignature.js";
import { ModeNotChosen, type CustomerServices, type QuotedCart } from "../customer/customerServices.js";
import { formatMinor } from "../customer/money.js";
import type { CatalogueItem, CatalogueSection } from "./catalogue.js";
import { CartScreen, type CartTotal } from "./CartScreen.js";
import { ItemScreen } from "./ItemScreen.js";
import { MenuScreen } from "./MenuScreen.js";
import type { OrderLine } from "./orderLine.js";

/**
 * LAZY, like the card form: Checkout is reached only from the Cart, so it stays
 * out of the entry a kitchen display downloads.
 */
const CheckoutScreen = lazy(() => import("../checkout/CheckoutScreen.js").then((module) => ({ default: module.CheckoutScreen })));

export interface CheckoutWiring {
  /** Only the modes the location declares. */
  readonly modes: readonly string[];
  /** The resolver's mode, or `null` while the diner must choose. */
  readonly initialMode: string | null;
  readonly readyInMinutes: number | null;
  /** `null` when card payment is not connected here. */
  readonly card: CardPayment | null;
}

export interface OrderScreensProps {
  readonly sections: readonly CatalogueSection[];
  /** `null` while the flow, the menu or the cart cannot be served yet. */
  readonly services: CustomerServices | null;
  /** Drawn above the Menu and Item screens (the storefront and status notices). */
  readonly header?: ReactNode;
  readonly checkout: CheckoutWiring;
  readonly onPlaced: (placed: PlacedOrder) => void;
}

type View =
  | { readonly kind: "menu" }
  | { readonly kind: "item"; readonly item: CatalogueItem; readonly lineId: string }
  | { readonly kind: "cart" }
  | { readonly kind: "checkout" };

/**
 * What a quote was taken for: each line's handle and quantity as well as the
 * cart signature. The services sync the server cart by line handle, so a line
 * removed and added again has the same signature but is a different server
 * cart, and only a fresh `priceCart` sends the delete and the add.
 */
function linesKey(lines: readonly OrderLine[]): string {
  return `${lines.map((line) => `${line.id}*${String(line.quantity)}`).join("|")}#${cartSignature(lines)}`;
}

type Quoted = {
  /** `linesKey` of the lines it priced. */
  readonly key: string;
  /** The services that priced it: a quote from another cart is no quote for this one. */
  readonly services: CustomerServices;
  readonly total: CartTotal;
  /** The full quote when it priced; Checkout opens on it (ORDR-228). */
  readonly priced: QuotedCart | null;
};

const WAITS_FOR_MODE = "Your total is worked out at checkout, once you choose how you want your order.";
const NOT_PRICED = "We could not work out your total just now. You can still go on to checkout.";

export function OrderScreens({ sections, services, header, checkout, onPlaced }: OrderScreensProps): ReactElement {
  const [view, setView] = useState<View>({ kind: "menu" });
  const [lines, setLines] = useState<readonly OrderLine[]>([]);
  const [quoted, setQuoted] = useState<Quoted | null>(null);
  const nextLine = useRef(0);

  const key = linesKey(lines);
  const current = quoted !== null && quoted.services === services && quoted.key === key ? quoted : null;
  const settled = current?.total.kind === "ok";

  // The SERVER total for "View cart" and the Cart, asked for only when these
  // lines have no settled quote and none is already on its way (ORDR-228).
  // Checkout prices for itself, once the mode it will be placed for is the
  // cart's. No answer is dropped on a view change: the services run one cart
  // call at a time, so answers land in the order they were asked, and one for
  // lines the diner has since changed is simply not `current`.
  const asking = useRef<{ readonly key: string; readonly services: CustomerServices } | null>(null);
  useEffect(() => {
    if (services === null || (view.kind !== "menu" && view.kind !== "cart") || lines.length === 0 || settled) {
      return;
    }
    const asked = linesKey(lines);
    if (asking.current !== null && asking.current.key === asked && asking.current.services === services) {
      return;
    }
    const ask = { key: asked, services };
    asking.current = ask;
    const done = (next: Quoted): void => {
      if (asking.current === ask) {
        asking.current = null;
      }
      setQuoted(next);
    };
    services.priceCart(lines).then(
      (priced) => done({ key: asked, services, total: { kind: "ok", text: formatMinor(priced.totalCents, priced.currency) }, priced }),
      (error: unknown) =>
        done({ key: asked, services, total: { kind: "pending", text: error instanceof ModeNotChosen ? WAITS_FOR_MODE : NOT_PRICED }, priced: null }),
    );
  }, [lines, services, view.kind, settled]);

  /** Checkout's fresh figure, or `null` when its mode changed and the old figure no longer holds. */
  const onCheckoutPriced = useCallback(
    (priced: QuotedCart | null, pricedLines: readonly OrderLine[]): void => {
      if (services === null) {
        return;
      }
      setQuoted(
        priced === null
          ? null
          : { key: linesKey(pricedLines), services, total: { kind: "ok", text: formatMinor(priced.totalCents, priced.currency) }, priced },
      );
    },
    [services],
  );

  if (services === null) {
    return <>{header}</>;
  }

  if (view.kind === "checkout") {
    return (
      <Suspense fallback={null}>
        <CheckoutScreen
          lines={lines}
          services={services}
          modes={checkout.modes}
          initialMode={checkout.initialMode}
          readyInMinutes={checkout.readyInMinutes}
          card={checkout.card}
          lastTotal={current?.priced ?? null}
          onPriced={(priced) => onCheckoutPriced(priced, lines)}
          onBack={() => setView({ kind: "menu" })}
          onPlaced={onPlaced}
        />
      </Suspense>
    );
  }

  if (view.kind === "cart") {
    return (
      <CartScreen
        lines={lines}
        total={current?.total ?? { kind: "pending", text: "Working out your total…" }}
        onQuantity={(lineId, quantity) =>
          setLines((all) => all.map((line) => (line.id === lineId ? { ...line, quantity } : line)))
        }
        onRemove={(lineId) => setLines((all) => all.filter((line) => line.id !== lineId))}
        onBack={() => setView({ kind: "menu" })}
        onCheckout={() => setView({ kind: "checkout" })}
      />
    );
  }

  const count = lines.reduce((sum, line) => sum + line.quantity, 0);

  return (
    <>
      {header}
      {view.kind === "item" ? (
        <ItemScreen
          key={view.item.offerRef}
          item={view.item}
          lineId={view.lineId}
          onAdd={(line) => {
            setLines((all) => [...all, line]);
            setView({ kind: "menu" });
          }}
          onBack={() => setView({ kind: "menu" })}
        />
      ) : (
        <MenuScreen
          sections={sections}
          cart={{ count, total: current?.total.kind === "ok" ? current.total.text : null }}
          onOpenItem={(item) => {
            nextLine.current += 1;
            setView({ kind: "item", item, lineId: `line-${String(nextLine.current)}` });
          }}
          onViewCart={() => setView({ kind: "cart" })}
        />
      )}
    </>
  );
}
